import { useState } from 'react';
import { Autocomplete, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { Alert, FlexColumn, TextField } from '@elementary/common-ui/components';
import useSnackbar from 'shared/hooks/useSnackbar';
import { IncidentIoAlertSource } from 'shared/types';
import * as api from 'shared/services/settingsApi';
import { INCIDENT_IO_TOKEN_PLACEHOLDER } from '../IncidentManagementTool/IncidentIoDialog';
import TestMessageSection from './TestMessageSection';

type Props = {
  sources: IncidentIoAlertSource[];
  onSuccess?: () => void;
  onReset?: () => void;
};

const IncidentIoTestSection = ({ sources, onSuccess, onReset }: Props) => {
  const [selectedSource, setSelectedSource] =
    useState<IncidentIoAlertSource | null>(null);
  const [customMessage, setCustomMessage] = useState<string>('');

  const { openSnackbar } = useSnackbar();

  const defaultMessage =
    'This is a test incident from Elementary Data to verify your incident.io integration is working correctly.';

  const testMutation = useMutation(
    async (data: {
      message?: string;
      sourceName?: string | null;
      token?: string | null;
      alertSourceConfigId?: string | null;
    }) => {
      const response = await api.sendIncidentIoTestIncident(data);
      return response;
    },
    {
      onSuccess: (response) => {
        if (response.success) {
          openSnackbar({
            message: 'Test incident sent successfully to incident.io!',
            variant: 'success',
          });
          onSuccess?.();
        } else {
          openSnackbar({
            message:
              response.message || 'Failed to send test incident to incident.io',
            variant: 'error',
          });
          onReset?.();
        }
      },
      onError: (error: MutationError) => {
        openSnackbar({
          message:
            error?.message ||
            error?.response?.data?.message ||
            'Failed to send test incident to incident.io',
          variant: 'error',
        });
        onReset?.();
      },
    },
  );

  const handleSendTest = () => {
    const source = selectedSource;
    if (!source) return;
    const message = customMessage.trim() || defaultMessage;
    const isPlaceholder =
      !source.token || source.token === INCIDENT_IO_TOKEN_PLACEHOLDER;
    testMutation.mutate({
      message,
      sourceName: source.name || undefined,
      token: isPlaceholder ? undefined : source.token,
      alertSourceConfigId: source.alertSourceConfigId || undefined,
    });
  };

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <Typography>
        Send a test incident to verify your incident.io integration is working
        correctly.
      </Typography>

      <Alert severity="warning">
        <strong>Important:</strong> This will create a real alert event in your
        incident.io account. You may need to manually resolve or close the alert
        after testing.
      </Alert>

      <Autocomplete
        options={sources.filter((s) => s.name)}
        value={selectedSource}
        onChange={(_, newValue) => setSelectedSource(newValue)}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        disabled={testMutation.isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select alert source"
            placeholder="e.g., my-source"
            fullWidth
            size="large"
          />
        )}
      />

      <TestMessageSection
        customMessage={customMessage}
        onCustomMessageChange={setCustomMessage}
        defaultMessage={defaultMessage}
        onSendTest={handleSendTest}
        isLoading={testMutation.isLoading}
        disabled={!selectedSource}
        messageLabel="Test incident message (optional)"
      />
    </FlexColumn>
  );
};

export default IncidentIoTestSection;
